import React, { useState, FC, useCallback } from 'react';

interface IContextState {
  showSelectColumn: boolean;
  setShowSelectColumn: (val: boolean) => void;
  customSelectableRowIds: any[];
  setCustomSelectableRowIds: (rows: any[]) => void;
  selectColPosition: 'start' | 'end';
  setSelectColPosition: (pos: 'start' | 'end') => void;
  selectedRowDataIds: number[];
  setSelectedRowDataIds: (ids: number[], isSelected: boolean) => void;
  selectedRowsOnAction: (callback: (selected: any[]) => void) => void;
  resetSelectable: () => void;
  selectAllRows: (ids: number[], isSelected: boolean) => void;
}

/**
 * For tables that should allow rows selection, we use
 * `SelectableTableContext` by which we control selectable tables
 *
 * We can set whether to show or hide select column,
 * filter the row data ids that could be selected,
 * set the select column position at start/end of table columns,
 * pass callback func to `selectedRowsOnAction` which will receive all selected row data ids
 * or
 * reset selectable table to initial state
 */
export const SelectableTableContext = React.createContext<IContextState>({
  showSelectColumn: false,
  setShowSelectColumn: (val: boolean) => {},
  customSelectableRowIds: [],
  setCustomSelectableRowIds: (rows: any[]) => {},
  selectColPosition: 'end',
  setSelectColPosition: (pos: 'start' | 'end') => {},
  selectedRowDataIds: [],
  setSelectedRowDataIds: (ids: number[], isSelected: boolean) => {},
  selectedRowsOnAction: (callback: (selected: any[]) => void) => {},
  resetSelectable: () => {},
  selectAllRows: (ids: number[], isSelected: boolean) => {},
});

interface IProps {
  children: React.ReactNode;
}

export const SelectableTableProvider: FC<IProps> = ({ children }: IProps) => {
  // Show/hide select column
  const [showSelectColumn, setSelectColumnFunction] = useState<boolean>(false);
  const setShowSelectColumn = useCallback(setSelectColumnFunction, []);

  // Pass row data IDs that can be selected
  const [customSelectableRowIds, setCustomSelectableRowIds] = useState<any[]>([]);

  // Set select column position
  const [selectColPosition, setSelectColPosition] = useState<'start' | 'end'>('end');

  // Keep selected row data IDs
  const [selectedRowDataIds, setSelectedRowDataIdsState] = useState<number[]>([]);

  const setSelectedRowDataIds = (ids: number[], isSelected: boolean) => {
    setSelectedRowDataIdsState(prevIds => {
      // If the rows are selected, add them to the list
      if (isSelected) {
        return Array.from(new Set([...prevIds, ...ids])); // Using a Set to avoid duplicates
      }
      // If the rows are deselected, remove them from the list
      return prevIds.filter(id => !ids.includes(id));
    });
  };

  // Select all rows that are possible to select
  const selectAllRows = (ids: number[], isSelected: boolean) => {
    setSelectedRowDataIds(ids, isSelected);
  };

  /**
   * Method to handle selected table row data IDs
   *
   * @param callback Function that should handle selected table row data IDs
   * (different from table row IDs)
   */
  const selectedRowsOnAction = (callback: (selected: any[]) => void) => {
    callback(selectedRowDataIds);
  };

  // Reset selectable table state to initial
  const resetSelectable = () => {
    setShowSelectColumn(false);
    setCustomSelectableRowIds([]);
    setSelectColPosition('end');
    setSelectedRowDataIds([], true);
    setSelectedRowDataIdsState([]);
  };

  return (
    <SelectableTableContext.Provider
      value={{
        showSelectColumn,
        setShowSelectColumn,
        customSelectableRowIds,
        setCustomSelectableRowIds,
        selectColPosition,
        setSelectColPosition,
        selectedRowDataIds,
        setSelectedRowDataIds,
        selectedRowsOnAction,
        resetSelectable,
        selectAllRows,
      }}
    >
      {children}
    </SelectableTableContext.Provider>
  );
};

export function useSelectableTableContext(): IContextState {
  return React.useContext(SelectableTableContext);
}
